import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { UserData } from "../generated/user_pb";
import { usernatorClient } from "./useFriends";
import type { RpcError } from "grpc-web";

/**
 * The three lists that make up "who follows whom" from the signed-in user's side, beyond the
 * plain following list `useFriends` already covers:
 *
 *  - followers        who follows me           (getFollowers)
 *  - incoming         asked to follow me       (listFollowRequests)
 *  - outgoing         I asked to follow them   (listOutgoingFollowRequests)
 *
 * Relationships are one-directional (see Saufdings/README.md's "Follows" section), so these are
 * genuinely independent sets - someone can follow me without me following them, and vice versa.
 *
 * `outgoing` is the one that was missing entirely before, and its absence caused a real bug rather
 * than just a gap: FriendsPage only excluded people you already *follow* from the "follow someone"
 * picker, so anyone with a request still pending stayed selectable. Pressing Follow again then hit
 * addFriend's idempotent insert and came back as an opaque AlreadyExists. With the outgoing list
 * loaded, those users can be excluded and shown as "Requested" instead.
 *
 * Refreshes on mount and whenever the window regains focus. Deliberately no polling interval: this
 * is a low-frequency social action, and a timer in every open tab would hammer Saufdings forever
 * for something a glance at the page already answers.
 */
export function useFollowGraph() {
    const { user } = useAuth();
    const [followers, setFollowers] = useState<UserData.AsObject[]>([]);
    const [incoming, setIncoming] = useState<UserData.AsObject[]>([]);
    const [outgoing, setOutgoing] = useState<UserData.AsObject[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const buildUserData = useCallback(() => {
        const u = new UserData();
        u.setUserid(user!.userId);
        u.setUsername(user!.username);
        u.setToken(user!.token);
        return u;
    }, [user]);

    const load = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        setError(null);
        try {
            // Independent calls, so run them together rather than serially - three round trips to
            // Saufdings back to back is a visible delay on a page that shows all three at once.
            const [followersRes, incomingRes, outgoingRes] = await Promise.all([
                usernatorClient.getFollowers(buildUserData(), {}),
                usernatorClient.listFollowRequests(buildUserData(), {}),
                usernatorClient.listOutgoingFollowRequests(buildUserData(), {}),
            ]);
            setFollowers(followersRes.getUserList().map((u: UserData) => u.toObject()));
            // listFollowRequests returns FollowRequestData wrappers; the other two return plain
            // users. Unwrap here so every consumer deals with one shape.
            setIncoming(
                incomingRes
                    .getRequestsList()
                    .map(r => r.getRequester()?.toObject())
                    .filter((u): u is UserData.AsObject => u !== undefined)
            );
            setOutgoing(outgoingRes.getUserList().map((u: UserData) => u.toObject()));
        } catch (err) {
            setError((err as RpcError).message ?? "Failed to load follow information.");
        } finally {
            setLoading(false);
        }
    }, [user, buildUserData]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        // Someone approving a request while this tab sat in the background is exactly the case a
        // manual reload was needed for before.
        const onFocus = () => load();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [load]);

    return { followers, incoming, outgoing, loading, error, reload: load, buildUserData };
}
